<template>
    <div class="note-sidebar">
        <div class="sidebar-section">
            <div class="sidebar-item sidebar-item--top" :class="{active: activeView === 'all'}" @click="selectView('all')">
                <span class="sidebar-item-icon">📋</span>
                <span class="sidebar-item-text">{{ $t('pages.module.NOTE.sidebar.all') }}</span>
                <span v-if="activeView === 'all'" class="sidebar-toggle" @click.stop="toggleIncludeShared" :title="$t('pages.module.NOTE.sidebar.includeShared')">
                    <span class="sidebar-toggle-track" :class="{'sidebar-toggle-track--on': includeSharedLocal}">
                        <span class="sidebar-toggle-thumb"></span>
                    </span>
                </span>
            </div>

            <div class="sidebar-item" :class="{active: activeView === 'shared'}" @click="selectView('shared')">
                <span class="sidebar-item-icon">👥</span>
                <span class="sidebar-item-text">{{ $t('pages.module.NOTE.sidebar.sharedWithMe') }}</span>
            </div>

            <div class="sidebar-item" :class="{active: activeView === 'drafts'}" @click="selectView('drafts')">
                <span class="sidebar-item-icon">📝</span>
                <span class="sidebar-item-text">{{ $t('pages.module.NOTE.sidebar.drafts') }}</span>
                <span class="sidebar-item-count" v-if="draftsCount > 0">{{ draftsCount }}</span>
            </div>
        </div>

        <div class="sidebar-scrollable">
        <div class="sidebar-section">
            <div class="sidebar-section-header">
                <span>{{ $t('pages.module.NOTE.sidebar.categories') }}</span>
                <div class="sidebar-header-actions">
                    <button v-if="flatParentIds.length" class="sidebar-header-btn" @mousedown.prevent="toggleCollapseAll" :title="allCategoriesCollapsed ? 'Expand all' : 'Collapse all'">
                        <svg width="12" height="12" viewBox="0 0 12 12"><path :d="allCategoriesCollapsed ? 'M4.5 2L9 6L4.5 10' : 'M2 4.5L6 9L10 4.5'" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </button>
                    <button class="sidebar-header-btn" @mousedown.prevent="startNewRootCategory" :title="$t('pages.module.NOTE.sidebar.newCategory')">
                        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                </div>
            </div>


            <div v-if="showNewCategory && !pendingParentId" class="sidebar-inline-form">
                <input ref="newCategoryInput" v-model="newCategoryName"
                       :placeholder="$t('pages.module.NOTE.sidebar.categoryName')"
                       @keydown.enter="addCategory"
                       @keydown.esc.stop="cancelNewCategory"
                       @blur="onNewCategoryBlur"
                       class="sidebar-inline-input"/>
            </div>


            <template v-for="cat in flatCategoryList" :key="cat.id">
                <div class="sidebar-item sidebar-category-item"
                     :class="{
                         active: activeView === 'category' && selectedCategoryId === cat.id,
                         'drag-over': dragOverCategoryId === cat.id && !dropIndicator,
                         'drop-before': dropIndicator === 'before-' + cat.id,
                         'drop-after': dropIndicator === 'after-' + cat.id
                     }"
                     :data-category-id="cat.id"
                     :style="{paddingLeft: (12 + cat.depth * 18) + 'px'}"
                     :draggable="!compact"
                     @click="onCategoryClick(cat)"
                     @contextmenu.prevent="$emit('categoryContextMenu', $event, cat)"
                     @dragstart="onCategoryDragStart($event, cat)"
                     @dragend="onCategoryDragEnd"
                     @touchstart.passive="onCategoryLongPressStart($event, cat)"
                     @touchend.passive="onCategoryLongPressEnd"
                     @dragover.prevent="onCategoryDragOver($event, cat)"
                     @dragleave="onCategoryDragLeave"
                     @drop="onCategoryDrop($event, cat)">
                    <button v-if="cat._hasChildren" class="category-chevron" :class="{'category-chevron--collapsed': collapsedIds.has(cat.id)}" @click.stop="toggleCollapse(cat.id)">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
                    </button>
                    <span v-else class="category-chevron-spacer"></span>
                    <span v-if="cat.color || cat._siblingHasColor" class="category-color-dot" :class="{'category-color-dot--empty': !cat.color}" :style="cat.color ? {background: cat.color} : null"></span>
                    <span class="sidebar-item-text" v-if="editingCategoryId !== cat.id">{{ cat.name }}</span>
                    <input v-else v-model="editingCategoryName" class="sidebar-inline-input sidebar-inline-input--edit"
                           @keydown.enter="saveRename(cat)" @keydown.esc.stop="cancelRename" @blur="cancelRename" ref="renameCategoryInput"/>
                    <span class="sidebar-item-count" v-if="cat.notes_count !== undefined">{{ cat.notes_count }}</span>
                </div>

                <div v-if="showNewCategory && pendingParentId === cat.id" class="sidebar-inline-form" :style="{paddingLeft: (10 + (cat.depth + 1) * 18) + 'px'}">
                    <input ref="newCategoryInput" v-model="newCategoryName"
                           :placeholder="$t('pages.module.NOTE.sidebar.categoryName')"
                           @keydown.enter="addCategory"
                           @keydown.esc.stop="cancelNewCategory"
                           @blur="onNewCategoryBlur"
                           class="sidebar-inline-input"/>
                </div>
            </template>
        </div>


        <div class="sidebar-section">
            <div class="sidebar-section-header">
                <span>{{ $t('pages.module.NOTE.sidebar.tags') }}</span>
                <div class="sidebar-header-actions">
                    <button class="sidebar-header-btn" @mousedown.prevent="startNewTag" :title="$t('pages.module.NOTE.sidebar.newTag')">
                        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                    </button>
                </div>
            </div>

            <div v-if="showNewTag" class="sidebar-inline-form">
                <input ref="newTagInput" v-model="newTagName"
                       :placeholder="$t('pages.module.NOTE.sidebar.tagName')"
                       @keydown.enter="addTag"
                       @keydown.esc.stop="showNewTag = false"
                       @blur="onNewTagBlur"
                       class="sidebar-inline-input"/>
            </div>

            <div v-for="tag in tags" :key="tag.id"
                 class="sidebar-item"
                 :class="{active: activeView === 'tag' && selectedTagId === tag.id, 'drag-over': dragOverTagId === tag.id}"
                 :draggable="!compact"
                 @click="onTagClick(tag)"
                 @contextmenu.prevent="$emit('tagContextMenu', $event, tag)"
                 @touchstart.passive="onTagLongPressStart($event, tag)"
                 @touchend.passive="onTagLongPressEnd"
                 @dragstart="onTagDragStart($event, tag)"
                 @dragover.prevent="onTagDragOver($event, tag)"
                 @dragleave="onTagDragLeave"
                 @drop="onTagDrop($event, tag)">
                <span class="sidebar-item-icon">#</span>
                <span class="sidebar-item-text">{{ tag.name }}</span>
            </div>
        </div>


        <div class="sidebar-section">
            <div class="sidebar-section-header">
                <span>{{ $t('pages.module.NOTE.sidebar.colors') }}</span>
            </div>
            <div class="sidebar-color-grid">
                <div v-for="color in noteColors" :key="color.id"
                     class="sidebar-color-dot"
                     :class="{active: selectedColorHex === color.hex}"
                     :style="{background: color.hex}"
                     :title="color.label"
                     @click="selectColor(color)">
                </div>
            </div>
        </div>
        </div>

        <div class="sidebar-section sidebar-section--bottom">
            <div class="sidebar-item sidebar-trash-item"
                 :class="{active: activeView === 'trash', 'drag-over-trash': dragOverTrash, 'trash-eaten': trashEating}"
                 @click="selectView('trash')"
                 @dragover.prevent="dragOverTrash = true"
                 @dragleave="dragOverTrash = false"
                 @drop="onTrashDrop($event)">
                <span class="sidebar-item-icon trash-icon">🗑️</span>
                <span class="sidebar-item-text">{{ $t('pages.module.NOTE.sidebar.trash') }}</span>
                <span class="sidebar-item-count" v-if="trashCount > 0">{{ trashCount }}</span>
                <div v-if="trashEating" class="trash-paper">
                    <svg width="20" height="24" viewBox="0 0 20 24" fill="none">
                        <path d="M2 0 L18 0 L20 2 L18 4 L20 8 L17 10 L19 14 L16 16 L18 20 L14 22 L16 24 L4 24 L6 22 L2 20 L4 16 L1 14 L3 10 L0 8 L2 4 L0 2 Z" class="trash-paper-path"/>
                    </svg>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const MAX_CATEGORY_DEPTH = 5

const NOTE_COLORS = [
    {id: 'red', hex: '#FF3B30', label: 'Kırmızı'},
    {id: 'orange', hex: '#FF9500', label: 'Turuncu'},
    {id: 'yellow', hex: '#FFCC00', label: 'Sarı'},
    {id: 'green', hex: '#34C759', label: 'Yeşil'},
    {id: 'blue', hex: '#007AFF', label: 'Mavi'},
    {id: 'purple', hex: '#AF52DE', label: 'Mor'},
    {id: 'pink', hex: '#FF2D55', label: 'Pembe'},
    {id: 'gray', hex: '#8E8E93', label: 'Gri'},
]

export default {
    name: 'NoteSidebar',
    props: {
        categoryTree: {type: Array, default: () => []},
        tags: {type: Array, default: () => []},
        draftsCount: {type: Number, default: 0},
        trashCount: {type: Number, default: 0},
        activeView: {type: String, default: 'all'},
        selectedCategoryId: {type: Number, default: null},
        selectedTagId: {type: Number, default: null},
        selectedColorHex: {type: String, default: null},
        includeShared: {type: Boolean, default: false},
        compact: {type: Boolean, default: false},
    },
    emits: ['selectView', 'selectCategory', 'selectTag', 'selectColor', 'toggleIncludeShared', 'addCategory', 'addTag', 'categoryContextMenu', 'tagContextMenu', 'noteDrop', 'noteDropTag', 'noteDropTrash', 'categoryDropTrash', 'tagDropTrash', 'categoryDrop', 'renameCategory', 'categoryReorder'],
    computed: {
        flatCategoryList() {
            const result = []
            const flatten = (items, depth = 0, parentId = null) => {
                for (const item of items) {
                    const hasChildren = item.children && item.children.length > 0
                    result.push({...item, depth, parent_id: parentId, _hasChildren: hasChildren, children: undefined})
                    if (hasChildren && !this.collapsedIds.has(item.id)) {
                        flatten(item.children, depth + 1, item.id)
                    }
                }
            }
            flatten(this.categoryTree)

            const groupHasColor = {}
            for (const cat of result) {
                const key = cat.parent_id ?? '_root'
                if (cat.color) groupHasColor[key] = true
            }
            for (const cat of result) {
                cat._siblingHasColor = !!groupHasColor[cat.parent_id ?? '_root']
            }

            return result
        },
        flatParentIds() {
            const ids = []
            const collect = (items) => {
                for (const item of items) {
                    if (item.children && item.children.length > 0) {
                        ids.push(item.id)
                        collect(item.children)
                    }
                }
            }
            collect(this.categoryTree)
            return ids
        },
        allCategoriesCollapsed() {
            if (!this.flatParentIds.length) return false
            return this.flatParentIds.every(id => this.collapsedIds.has(id))
        },
    },
    data() {
        return {
            noteColors: NOTE_COLORS,
            includeSharedLocal: this.includeShared,
            showNewCategory: false,
            newCategoryName: '',
            showNewTag: false,
            newTagName: '',
            dragOverCategoryId: null,
            draggingCategoryId: null,
            dragOverTagId: null,
            dragOverTrash: false,
            trashEating: false,
            dropIndicator: null,
            editingCategoryId: null,
            editingCategoryName: '',
            pendingParentId: null,
            collapsedIds: new Set(),
        }
    },
    watch: {
        includeShared(val) {
            this.includeSharedLocal = val
        },
        showNewCategory(val) {
            if (val) this.$nextTick(() => {
                const ref = this.$refs.newCategoryInput
                const el = Array.isArray(ref) ? ref[0] : ref
                el?.focus()
            })
        },
        showNewTag(val) {
            if (val) this.$nextTick(() => this.$refs.newTagInput?.focus())
        },
    },
    methods: {
        toggleCollapse(catId) {
            const next = new Set(this.collapsedIds)
            if (next.has(catId)) next.delete(catId)
            else next.add(catId)
            this.collapsedIds = next
        },
        toggleCollapseAll() {
            if (this.allCategoriesCollapsed) {
                this.collapsedIds = new Set()
            } else {
                this.collapsedIds = new Set(this.flatParentIds)
            }
        },
        toggleIncludeShared() {
            this.includeSharedLocal = !this.includeSharedLocal
            this.$emit('toggleIncludeShared', this.includeSharedLocal)
        },
        selectView(view) {
            this.$emit('selectView', view)
        },
        selectCategory(cat) {
            if (this.editingCategoryId) return
            this.$emit('selectCategory', cat)
        },
        onCategoryClick(cat) {
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            this.selectCategory(cat)
        },
        selectTag(tag) {
            this.$emit('selectTag', tag)
        },
        onTagClick(tag) {
            if (this._longPressedTag) {
                this._longPressedTag = false
                return
            }
            this.selectTag(tag)
        },
        selectColor(color) {
            if (this.selectedColorHex === color.hex) {
                this.$emit('selectColor', null)
            } else {
                this.$emit('selectColor', color.hex)
            }
        },
        hasActiveInput() {
            return this.showNewCategory || this.showNewTag || this.editingCategoryId !== null
        },
        dismissActiveInput() {
            if (this.showNewCategory) { this.cancelNewCategory(); return true }
            if (this.showNewTag) { this.showNewTag = false; return true }
            if (this.editingCategoryId) { this.cancelRename(); return true }
            return false
        },
        startNewRootCategory() {
            this._skipCatBlur = true
            this.pendingParentId = null
            this.newCategoryName = ''
            this.showNewCategory = true
            this.$nextTick(() => { this._skipCatBlur = false })
        },
        cancelNewCategory() {
            this.showNewCategory = false
            this.newCategoryName = ''
            this.pendingParentId = null
        },
        onNewCategoryBlur() {
            if (this._skipCatBlur) return
            this._catBlurTimer = setTimeout(() => this.cancelNewCategory(), 0)
        },
        onNewTagBlur() {
            this._tagBlurTimer = setTimeout(() => { this.showNewTag = false; this.newTagName = '' }, 0)
        },
        addCategory() {
            clearTimeout(this._catBlurTimer)
            if (!this.newCategoryName.trim()) return
            this.$emit('addCategory', {name: this.newCategoryName.trim(), parent_id: this.pendingParentId || null})
            this.newCategoryName = ''
            this.showNewCategory = false
            this.pendingParentId = null
        },
        startNewTag() {
            clearTimeout(this._tagBlurTimer)
            this.newTagName = ''
            this.showNewTag = true
        },
        addTag() {
            clearTimeout(this._tagBlurTimer)
            if (!this.newTagName.trim()) return
            this.$emit('addTag', {name: this.newTagName.trim()})
            this.newTagName = ''
            this.showNewTag = false
        },
        startRename(category) {
            this.editingCategoryId = category.id
            this.editingCategoryName = category.name
            this.$nextTick(() => {
                const inputs = this.$refs.renameCategoryInput
                if (inputs) {
                    const input = Array.isArray(inputs) ? inputs[0] : inputs
                    input?.focus()
                    input?.select()
                }
            })
        },
        saveRename(category) {
            if (!this.editingCategoryName.trim()) {
                this.cancelRename()
                return
            }
            this.$emit('renameCategory', {id: category.id, name: this.editingCategoryName.trim()})
            this.cancelRename()
        },
        cancelRename() {
            this.editingCategoryId = null
            this.editingCategoryName = ''
        },
        isDescendantOf(categoryId, ancestorId) {
            const find = (items) => {
                for (const item of items) {
                    if (item.id === ancestorId) return hasDescendant(item, categoryId)
                    if (item.children && item.children.length) {
                        const r = find(item.children)
                        if (r !== null) return r
                    }
                }
                return null
            }
            const hasDescendant = (node, targetId) => {
                if (!node.children) return false
                for (const child of node.children) {
                    if (child.id === targetId) return true
                    if (hasDescendant(child, targetId)) return true
                }
                return false
            }
            return find(this.categoryTree) === true
        },
        getSubtreeDepth(categoryId) {
            const calcDepth = (node) => {
                if (!node.children || !node.children.length) return 0
                let max = 0
                for (const child of node.children) max = Math.max(max, 1 + calcDepth(child))
                return max
            }
            const find = (items) => {
                for (const item of items) {
                    if (item.id === categoryId) return calcDepth(item)
                    if (item.children && item.children.length) {
                        const r = find(item.children)
                        if (r !== null) return r
                    }
                }
                return null
            }
            return find(this.categoryTree) ?? 0
        },
        startNewSubCategory(parentCategory) {
            if (parentCategory.depth + 1 >= MAX_CATEGORY_DEPTH) {
                this.eventBus.$emit('showConfirmDialog', {
                    title: this.$t('pages.module.NOTE.sidebar.depthLimitTitle'),
                    message: this.$t('pages.module.NOTE.sidebar.depthLimitAdd'),
                    confirmText: this.$t('pages.module.NOTE.sidebar.depthLimitConfirm'),
                    cancelText: this.$t('pages.module.NOTE.sidebar.depthLimitCancel'),
                    confirmType: 'primary',
                    onConfirm: () => {
                        this._openSubCategoryForm(parentCategory.parent_id || null)
                    },
                })
                return
            }
            this._openSubCategoryForm(parentCategory.id)
        },
        _showDepthLimitDialog() {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.module.NOTE.sidebar.depthLimitTitle'),
                message: this.$t('pages.module.NOTE.sidebar.depthLimitMove'),
                confirmText: this.$t('common.ok'),
                confirmType: 'default',
                singleButton: true,
            })
        },
        _openSubCategoryForm(parentId) {
            this._skipCatBlur = true
            this.showNewCategory = true
            this.newCategoryName = ''
            this.pendingParentId = parentId
            this.$nextTick(() => {
                this._skipCatBlur = false
                const ref = this.$refs.newCategoryInput
                const el = Array.isArray(ref) ? ref[0] : ref
                el?.focus()
            })
        },
        onCategoryLongPressStart(event, cat) {
            const touch = event.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressed = false
            this._longPressReady = false
            this._lpCat = cat
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
            }, 600)
        },
        onCategoryLongPressEnd(event) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._longPressReady) {
                const touch = event.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.$emit('categoryContextMenu', {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}}, this._lpCat)
                    return
                }
            }
            this._longPressReady = false
        },
        onTagLongPressStart(event, tag) {
            const touch = event.touches[0]
            this._lpTagStartX = touch.clientX
            this._lpTagStartY = touch.clientY
            this._longPressedTag = false
            this._longPressTagReady = false
            this._lpTag = tag
            this._longPressTagTimer = setTimeout(() => {
                this._longPressTagReady = true
                this._longPressTagTimer = null
            }, 600)
        },
        onTagLongPressEnd(event) {
            clearTimeout(this._longPressTagTimer)
            this._longPressTagTimer = null
            if (this._longPressTagReady) {
                const touch = event.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpTagStartX)
                const dy = Math.abs(touch.clientY - this._lpTagStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressTagReady = false
                    this._longPressedTag = true
                    this.$emit('tagContextMenu', {clientX: this._lpTagStartX, clientY: this._lpTagStartY, preventDefault() {}}, this._lpTag)
                    return
                }
            }
            this._longPressTagReady = false
        },
        onCategoryDragStart(event, cat) {
            this.draggingCategoryId = cat.id
            event.dataTransfer.setData('categoryId', String(cat.id))
            event.dataTransfer.effectAllowed = 'move'

            if ('ontouchstart' in window) {
                const el = event.currentTarget
                const ghost = el.cloneNode(true)
                const rect = el.getBoundingClientRect()
                ghost.style.cssText = 'position:fixed;top:-1000px;left:-1000px;width:' + rect.width + 'px;background:var(--note-sidebar-bg,#f2f2f7);opacity:0.9;border-radius:6px;box-shadow:0 4px 16px rgba(0,0,0,0.15);pointer-events:none;'
                document.body.appendChild(ghost)
                event.dataTransfer.setDragImage(ghost, event.clientX - rect.left, event.clientY - rect.top)
                this._dragGhost = ghost
            }
        },
        onCategoryDragOver(event, cat) {
            const noteId = event.dataTransfer.types.includes('noteid') || this.draggingCategoryId === null
            if (!this.draggingCategoryId) {
                this.dragOverCategoryId = cat.id
                this.dropIndicator = null
                return
            }
            if (this.draggingCategoryId === cat.id) return
            if (this.isDescendantOf(cat.id, this.draggingCategoryId)) return
            const rect = event.currentTarget.getBoundingClientRect()
            const y = event.clientY - rect.top
            const threshold = rect.height * 0.3
            if (y < threshold) {
                this.dropIndicator = 'before-' + cat.id
                this.dragOverCategoryId = null
            } else if (y > rect.height - threshold) {
                this.dropIndicator = 'after-' + cat.id
                this.dragOverCategoryId = null
            } else {
                this.dropIndicator = null
                this.dragOverCategoryId = cat.id
            }
        },
        onCategoryDragLeave() {
            this.dragOverCategoryId = null
            this.dropIndicator = null
        },
        onCategoryDrop(event, cat) {
            const indicator = this.dropIndicator
            this.dragOverCategoryId = null
            this.dropIndicator = null

            const noteId = event.dataTransfer.getData('noteId')
            const categoryId = event.dataTransfer.getData('categoryId')

            if (noteId) {
                this.$emit('noteDrop', {noteId: noteId.startsWith('temp_') ? noteId : parseInt(noteId), categoryId: cat.id})
                this.draggingCategoryId = null
                return
            }

            if (!categoryId || parseInt(categoryId) === cat.id) {
                this.draggingCategoryId = null
                return
            }

            const draggedId = parseInt(categoryId)

            if (this.isDescendantOf(cat.id, draggedId)) {
                this.draggingCategoryId = null
                this.cleanupDragGhost()
                return
            }

            const subtreeDepth = this.getSubtreeDepth(draggedId)

            if (indicator && indicator.startsWith('before-') || indicator && indicator.startsWith('after-')) {
                const position = indicator.startsWith('before-') ? 'before' : 'after'
                const siblings = this.getSiblings(cat)
                const targetIndex = siblings.findIndex(s => s.id === cat.id)
                const insertIndex = position === 'after' ? targetIndex + 1 : targetIndex
                const draggedCat = this.flatCategoryList.find(c => c.id === draggedId)
                const newParentId = cat.parent_id || null
                if (draggedCat && draggedCat.parent_id !== newParentId) {
                    if (cat.depth + subtreeDepth >= MAX_CATEGORY_DEPTH) {
                        this._showDepthLimitDialog()
                        this.draggingCategoryId = null
                        this.cleanupDragGhost()
                        return
                    }
                }
                const reordered = siblings.filter(s => s.id !== draggedId)
                reordered.splice(Math.min(insertIndex, reordered.length), 0, {id: draggedId})
                const updates = reordered.map((item, idx) => ({id: item.id, sort: idx, parent_id: newParentId}))
                const draggedUpdate = updates.find(u => u.id === draggedId)
                if (draggedUpdate && draggedCat && draggedCat.parent_id !== newParentId) {
                    draggedUpdate.parentChanged = true
                }
                this.$emit('categoryReorder', updates)
            } else {
                if (cat.depth + 1 + subtreeDepth >= MAX_CATEGORY_DEPTH) {
                    this._showDepthLimitDialog()
                    this.draggingCategoryId = null
                    this.cleanupDragGhost()
                    return
                }
                this.$emit('categoryDrop', {categoryId: draggedId, parentId: cat.id})
            }
            this.draggingCategoryId = null
            this.cleanupDragGhost()
        },
        onCategoryDragEnd(event) {
            if (this._longPressReady) {
                const dx = Math.abs(event.clientX - this._lpStartX)
                const dy = Math.abs(event.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.$emit('categoryContextMenu', {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}}, this._lpCat)
                }
            }
            this._longPressReady = false
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            this.draggingCategoryId = null
            this.dragOverCategoryId = null
            this.dropIndicator = null
            this.cleanupDragGhost()
        },
        cleanupDragGhost() {
            if (this._dragGhost) {
                this._dragGhost.remove()
                this._dragGhost = null
            }
        },
        getSiblings(cat) {
            const parentId = cat.parent_id || null
            return this.flatCategoryList.filter(c => (c.parent_id || null) === parentId)
        },
        onTagDragStart(event, tag) {
            event.dataTransfer.setData('tagId', String(tag.id))
            event.dataTransfer.effectAllowed = 'move'
        },
        onTagDragOver(event, tag) {
            if (event.dataTransfer.types.includes('noteids')) {
                this.dragOverTagId = null
                return
            }
            this.dragOverTagId = tag.id
        },
        onTagDragLeave() {
            this.dragOverTagId = null
        },
        onTagDrop(event, tag) {
            this.dragOverTagId = null
            const noteIds = event.dataTransfer.getData('noteIds')
            if (noteIds) return
            const noteId = event.dataTransfer.getData('noteId')
            if (!noteId) return
            this.$emit('noteDropTag', {noteId: noteId.startsWith('temp_') ? noteId : parseInt(noteId), tagId: tag.id})
        },
        onTrashDrop(event) {
            this.dragOverTrash = false
            const noteId = event.dataTransfer.getData('noteId')
            const categoryId = event.dataTransfer.getData('categoryId')
            const tagId = event.dataTransfer.getData('tagId')
            if (!noteId && !categoryId && !tagId) return
            this.trashEating = true
            setTimeout(() => {
                this.trashEating = false
                if (noteId) {
                    this.$emit('noteDropTrash', {noteId: noteId.startsWith('temp_') ? noteId : parseInt(noteId)})
                } else if (categoryId) {
                    this.$emit('categoryDropTrash', {categoryId: parseInt(categoryId)})
                } else if (tagId) {
                    this.$emit('tagDropTrash', {tagId: parseInt(tagId)})
                }
            }, 600)
        },
    }
}
</script>

<style scoped>
.note-sidebar {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    padding: 8px 0 0;
    background: var(--note-sidebar-bg, #f2f2f7);
    user-select: none;
}

.sidebar-scrollable {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.sidebar-section {
    padding: 4px 0;
}

.sidebar-section--bottom {
    flex-shrink: 0;
    border-top: 1px solid var(--panel-border, #e5e5ea);
    padding: 8px 0;
}

.sidebar-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--note-group-header, #8e8e93);
    letter-spacing: 0.5px;
}

.sidebar-header-actions {
    display: flex;
    gap: 2px;
    align-items: center;
    margin-right: 4px;
}

.sidebar-header-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: none;
    border: none;
    color: var(--note-group-header, #8e8e93);
    cursor: pointer;
    padding: 0;
    border-radius: 4px;
    transition: color 0.15s, background 0.15s;
}

.sidebar-header-btn:hover {
    color: var(--panel-accent-text);
    background: var(--panel-selected-bg);
}

.sidebar-item {
    display: flex;
    align-items: center;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 13px;
    color: var(--panel-text-primary, #1c1c1e);
    border-radius: 6px;
    margin: 1px 6px;
    gap: 6px;
    transition: background 0.15s;
}

.sidebar-item--top {
    margin-bottom: 4px;
}

.sidebar-item:hover {
    background: var(--note-selected-sidebar);
}

.sidebar-item.active {
    background: var(--note-selected-sidebar);
    color: var(--panel-accent-text);
    font-weight: 500;
}

.sidebar-item.drag-over {
    background: rgba(var(--panel-accent-rgb), 0.15);
    outline: 2px solid var(--panel-accent);
    outline-offset: -2px;
}

.sidebar-item.drag-over-trash {
    background: rgba(255, 59, 48, 0.12);
    outline: 2px solid #FF3B30;
    outline-offset: -2px;
    color: #FF3B30;
}

.sidebar-trash-item {
    position: relative;
    overflow: visible;
}

.trash-paper {
    position: absolute;
    left: 11px;
    top: -20px;
    transform-origin: center center;
    pointer-events: none;
    animation: paperCrumple 0.6s ease-in forwards;
    z-index: 10;
}

@keyframes paperCrumple {
    0% {
        opacity: 1;
        transform: translateY(-14px) scale(1) rotate(0deg);
    }
    25% {
        opacity: 1;
        transform: translateY(-4px) scale(0.75) rotate(12deg);
    }
    50% {
        opacity: 0.9;
        transform: translateY(4px) scale(0.45) rotate(-15deg);
    }
    75% {
        opacity: 0.6;
        transform: translateY(10px) scale(0.2) rotate(20deg);
    }
    100% {
        opacity: 0;
        transform: translateY(14px) scale(0.05) rotate(-10deg);
    }
}

.trash-eaten .trash-icon {
    animation: trashBounce 0.4s ease 0.35s;
}

@keyframes trashBounce {
    0% { transform: scale(1); }
    30% { transform: scale(1.3); }
    60% { transform: scale(0.9); }
    100% { transform: scale(1); }
}

.trash-paper-path {
    fill: #c7c7cc;
    stroke: #8e8e93;
    stroke-width: 0.8;
}

:global(html.dark) .trash-paper-path {
    fill: #3a3a3c;
    stroke: #636366;
}

.sidebar-item-icon {
    width: 18px;
    text-align: center;
    font-size: 14px;
    flex-shrink: 0;
}

.sidebar-item-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sidebar-item-count {
    font-size: 11px;
    color: var(--panel-text-secondary, #8e8e93);
    background: var(--panel-border, #e5e5ea);
    padding: 1px 6px;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
}

.category-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    background: none;
    color: var(--panel-text-secondary, #8e8e93);
    cursor: pointer;
    flex-shrink: 0;
    transition: transform 0.15s;
    border-radius: 3px;
}

.category-chevron:hover {
    color: var(--panel-text-primary, #1c1c1e);
}

.category-chevron--collapsed {
    transform: rotate(-90deg);
}

.category-chevron-spacer {
    width: 16px;
    flex-shrink: 0;
}

.category-color-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.category-color-dot--empty {
    border: 1.5px solid var(--panel-text-secondary, #8e8e93);
    box-sizing: border-box;
}


.sidebar-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;
    margin-left: auto;
}

.sidebar-toggle-track {
    position: relative;
    width: 28px;
    height: 16px;
    background: var(--panel-border, #e5e5ea);
    border-radius: 8px;
    transition: background 0.2s ease;
}

.sidebar-toggle-track--on {
    background: var(--panel-accent);
}

.sidebar-toggle-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 12px;
    height: 12px;
    background: #ffffff;
    border-radius: 50%;
    transition: transform 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}

.sidebar-toggle-track--on .sidebar-toggle-thumb {
    transform: translateX(12px);
}

.sidebar-color-grid {
    display: flex;
    gap: 6px;
    padding: 6px 12px;
    flex-wrap: wrap;
}

.sidebar-color-dot {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
    border: 2px solid transparent;
    box-sizing: border-box;
}

.sidebar-color-dot:hover {
    transform: scale(1.2);
}

.sidebar-color-dot.active {
    border-color: var(--panel-bg, #ffffff);
    box-shadow: 0 0 0 2px #636366;
    transform: scale(1.15);
}

:global(html.dark) .sidebar-color-dot.active {
    border-color: var(--panel-bg, #1c1c1e);
    box-shadow: 0 0 0 2px #98989f;
}

.sidebar-category-item {
    position: relative;
}

.sidebar-category-item.drop-before::before,
.sidebar-category-item.drop-after::after {
    content: '';
    position: absolute;
    left: 10px;
    right: 10px;
    height: 2px;
    background: var(--panel-accent);
    border-radius: 1px;
    pointer-events: none;
}

.sidebar-category-item.drop-before::before {
    top: -1px;
}

.sidebar-category-item.drop-after::after {
    bottom: -1px;
}

.sidebar-inline-form {
    padding: 2px 10px;
}

.sidebar-inline-input {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid var(--panel-accent);
    border-radius: 4px;
    font-size: 12px;
    background: var(--panel-bg, #ffffff);
    color: var(--panel-text-primary, #1c1c1e);
    outline: none;
}

.sidebar-inline-input--edit {
    padding: 2px 4px;
    margin: -2px 0;
}


.note-sidebar::-webkit-scrollbar {
    width: 4px;
}
.note-sidebar::-webkit-scrollbar-track {
    background: transparent;
}
.note-sidebar::-webkit-scrollbar-thumb {
    background: var(--panel-border, #e5e5ea);
    border-radius: 4px;
}

</style>
